import React, { useState, useMemo, useContext, useEffect } from 'react'
import ReactDOM from 'react-dom'
import { useQuery, useLazyQuery } from '@apollo/client'
import styles from './HomeView.module.scss'
import UserContext from '../context/UserContext'
import useLearnerContext from '../hooks/useLearnerContext'
import { getLanguageAcronym } from '../languages'
import flags from '../flags'
import curricula from '../data/curricula'
import { getBuiltinLesson } from '../data/lessons'
import { GET_LANGUAGE_TREE_QUERY, MY_ASSIGNMENTS_QUERY, GET_DUE_COUNTS_QUERY, GET_MEETING_WITH_CONTENT_QUERY } from '../query/gqlQueries'
import StudentLessonView from './StudentLessonView'
import { ActionTiles } from './LaunchGrid'

import CloudIcon from '@mui/icons-material/Cloud'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import ScienceIcon from '@mui/icons-material/Science'
import SpellcheckIcon from '@mui/icons-material/Spellcheck'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import PersonIcon from '@mui/icons-material/Person'
import AssignmentIcon from '@mui/icons-material/Assignment'
import ReplayIcon from '@mui/icons-material/Replay'
import LightModeIcon from '@mui/icons-material/LightMode'
import DarkModeIcon from '@mui/icons-material/DarkMode'

// Light/dark, shared with the lesson and Special Mode views through the same
// localStorage key — flip it here and the lesson opens in that theme too.
const THEME_KEY = 'lessonTheme'
const readTheme = () => { try { const t = localStorage.getItem(THEME_KEY); return t === 'light' || t === 'dark' ? t : 'dark' } catch { return 'dark' } }

// The default view: where you are, what to do next, and one tap to start.
// The word cloud stays behind it — "Word cloud" swaps back to it.
//
// Reads: the profile (languages + levels), the Skill Tree for the focus language
// (what's done, what's unlocked), homework assigned to you, and what's due for
// review. Everything degrades to a friendly empty state if the backend hasn't
// shipped a query yet.

// Shown before the learner's profile names a language to learn.
const DEFAULT_LANG_NAME = 'English'
const DEFAULT_LANG_CODE = 'eng'

const LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2']
const lvlIdx = (l) => { const i = LEVELS.indexOf(String(l || '').toUpperCase()); return i < 0 ? 0 : i }
const parseObj = (raw) => { try { const o = JSON.parse(raw || 'null'); return o && Array.isArray(o.sections) ? o : null } catch { return null } }
const greeting = () => { const h = new Date().getHours(); return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening' }
const fmtDue = (d) => { if (!d) return ''; const t = new Date(d); return isNaN(t) ? d : t.toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) }

// `actions`: the app shell's registry — every feature, grouped and already
// filtered by role — laid out at the bottom as "Everything".
export default function HomeView({ voiceSpeed, actions = [], onShowCloud, onOpenTree, onOpenSpecialMode, onOpenVerbLab, onOpenAiLesson, onOpenHomework, onOpenReview, onOpenProfile }) {
  const { username } = useContext(UserContext)
  const { user, learning } = useLearnerContext(username)
  const langs = useMemo(() => (learning || []).filter(l => l && l.name), [learning])

  // Focus language: the first one on the profile unless the learner switches.
  // Falls back to English when the profile names no language yet — the name and
  // the code are a pair, so change both together.
  const [focus, setFocus] = useState('')
  const focusName = focus || langs[0]?.name || DEFAULT_LANG_NAME
  const code = getLanguageAcronym(focusName) || DEFAULT_LANG_CODE
  const focusLevel = langs.find(l => l.name === focusName)?.level || ''

  const tree = useQuery(GET_LANGUAGE_TREE_QUERY, { variables: { langCode: code }, skip: !username, fetchPolicy: 'cache-and-network', errorPolicy: 'all' })
  const hw = useQuery(MY_ASSIGNMENTS_QUERY, { variables: {}, skip: !username, fetchPolicy: 'cache-and-network', errorPolicy: 'ignore' })
  // Due counts per language being learned (the server buckets by the
  // non-native side of each pair) — the row shows the focus language's.
  const due = useQuery(GET_DUE_COUNTS_QUERY, { variables: {}, skip: !username, fetchPolicy: 'cache-and-network', errorPolicy: 'ignore' })
  const [fetchMeeting] = useLazyQuery(GET_MEETING_WITH_CONTENT_QUERY, { fetchPolicy: 'network-only' })

  // The tree if it has been seeded; otherwise the built-in curriculum as a
  // preview (nothing completed yet, first topics open).
  const topics = useMemo(() => {
    const live = tree.data?.getLanguageTree || []
    if (live.length) return live
    return (curricula[code]?.topics || []).map(t => ({ ...t, id: null, completed: false, lessonMeetingId: null }))
  }, [tree.data, code])
  const seeded = (tree.data?.getLanguageTree || []).length > 0
  const doneSlugs = useMemo(() => new Set(topics.filter(t => t.completed).map(t => t.slug)), [topics])
  const upNext = useMemo(() => topics
    .filter(t => !t.completed && (t.requires || []).every(r => doneSlugs.has(r)))
    .sort((a, b) => lvlIdx(a.level) - lvlIdx(b.level) || (a.order || 0) - (b.order || 0))
    .slice(0, 4), [topics, doneSlugs])
  const total = topics.length
  const done = doneSlugs.size
  // Level you are working at: the profile level, else the lowest level with an unfinished topic.
  const workingLevel = focusLevel || upNext[0]?.level || 'A1'
  const levelTopics = topics.filter(t => t.level === workingLevel)
  const levelDone = levelTopics.filter(t => t.completed).length
  const pct = levelTopics.length ? Math.round((levelDone / levelTopics.length) * 100) : 0

  const toDo = (hw.data?.myAssignments || []).filter(a => a.status === 'assigned')
  const dueCount = (due.data?.getDueCounts || []).find(c => c && c.lang === code)?.count || 0

  // Open an assigned lesson right here, on top of the dashboard.
  const [lesson, setLesson] = useState(null)
  const [openErr, setOpenErr] = useState('')
  const openAssignment = async (a) => {
    setOpenErr('')
    try {
      const r = await fetchMeeting({ variables: { identity: a.meetingId } })
      const l = parseObj(r.data?.getMeetingWithContent?.meeting?.properties?.lessonData)
      if (l) setLesson({ lesson: l, meetingId: a.meetingId })
      else setOpenErr('That lesson has no content yet.')
    } catch (e) { setOpenErr(e.message) }
  }
  useEffect(() => { setOpenErr('') }, [focusName])

  const firstName = (user?.name || username || '').split(' ')[0]

  const [theme, setTheme] = useState(readTheme)
  const toggleTheme = () => setTheme(t => {
    const next = t === 'dark' ? 'light' : 'dark'
    try { localStorage.setItem(THEME_KEY, next) } catch { /* private mode */ }
    return next
  })

  return (
    <>
      <div className={`${styles.home} ${styles[theme]}`}>
        <div className={styles.inner}>
          <div className={styles.top}>
            <div>
              <div className={styles.brand}>Today</div>
              <h1 className={styles.greet}>{greeting()}{firstName ? `, ${firstName}` : ''}.</h1>
            </div>
            <div className={styles.topRight}>
              <button className={styles.iconBtn} onClick={toggleTheme} title={theme === 'dark' ? 'Light mode' : 'Dark mode'} aria-label="Toggle light or dark mode">
                {theme === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
              </button>
              <button className={styles.cloudBtn} onClick={onShowCloud} title="Show the word cloud behind this view">
                <CloudIcon fontSize="small" /> Word cloud
              </button>
            </div>
          </div>

          {/* ── languages & level ── */}
          <div className={styles.langRow}>
            {langs.map(l => {
              const c = getLanguageAcronym(l.name)
              return (
                <button key={l.code || l.name} className={`${styles.langChip} ${l.name === focusName ? styles.langChipActive : ''}`} onClick={() => setFocus(l.name)}>
                  <span className={styles.flag}>{flags[c] || '🌐'}</span>{l.name}{l.level ? <span className={styles.lvl}>{l.level}</span> : null}
                </button>
              )
            })}
            {langs.length === 0 && (
              <button className={styles.langChip} onClick={onOpenProfile}>＋ Tell me what you're learning</button>
            )}
          </div>

          <div className={styles.grid}>
            {/* ── where you are ── */}
            <section className={`${styles.card} ${styles.cardLevel}`}>
              <div className={styles.cardHead}><span className={styles.cardLabel}>Your level · {focusName}</span></div>
              <div className={styles.levelBig}>{workingLevel}<span className={styles.levelName}>{{ A1: 'Beginner', A2: 'Elementary', B1: 'Intermediate', B2: 'Upper intermediate', C1: 'Advanced', C2: 'Mastery' }[workingLevel] || ''}</span></div>
              <div className={styles.bar}><div className={styles.barFill} style={{ width: `${pct}%` }} /></div>
              <div className={styles.barNote}>{levelDone} of {levelTopics.length} {workingLevel} topics done · {done}/{total} overall{!seeded ? ' · tree not loaded yet' : ''}</div>
              <div className={styles.levelSteps}>
                {LEVELS.map(L => {
                  const ts = topics.filter(t => t.level === L)
                  const d = ts.filter(t => t.completed).length
                  const state = ts.length && d === ts.length ? styles.stepDone : L === workingLevel ? styles.stepNow : ''
                  return <span key={L} className={`${styles.step} ${state}`} title={`${d}/${ts.length}`}>{L}</span>
                })}
              </div>
            </section>

            {/* ── what to do next ── */}
            <section className={`${styles.card} ${styles.cardNext}`}>
              <div className={styles.cardHead}>
                <span className={styles.cardLabel}>Up next</span>
                <button className={styles.linkBtn} onClick={() => onOpenTree(focusName, null)}><AccountTreeIcon fontSize="inherit" /> whole tree</button>
              </div>
              {tree.loading && !topics.length ? <div className={styles.muted}>Loading…</div>
                : upNext.length === 0 ? <div className={styles.muted}>Everything unlocked is done — open the tree to see what's next.</div>
                  : upNext.map((t, i) => {
                    const authored = !!getBuiltinLesson(code, t.slug)
                    return (
                      <button key={t.slug} className={`${styles.topic} ${i === 0 ? styles.topicFirst : ''}`} onClick={() => onOpenTree(focusName, t.slug)}>
                        <span className={styles.topicLvl}>{t.level}</span>
                        <span className={styles.topicBody}>
                          <span className={styles.topicTitle}>{t.title}</span>
                          {t.blurb ? <span className={styles.topicBlurb}>{t.blurb}</span> : null}
                        </span>
                        <span className={styles.topicCta}>{i === 0 ? (t.lessonMeetingId || authored ? 'Continue →' : 'Start →') : '→'}</span>
                      </button>
                    )
                  })}
            </section>

            {/* ── homework ── */}
            <section className={styles.card}>
              <div className={styles.cardHead}>
                <span className={styles.cardLabel}>Homework</span>
                <button className={styles.linkBtn} onClick={onOpenHomework}><AssignmentIcon fontSize="inherit" /> all</button>
              </div>
              {toDo.length === 0
                ? <div className={styles.muted}>Nothing set for you right now.</div>
                : toDo.slice(0, 4).map(a => (
                  <div key={a.meetingId} className={styles.hwRow}>
                    <span className={styles.hwBody}>
                      <span className={styles.hwTitle}>{a.title}</span>
                      <span className={styles.hwMeta}>from {a.teacher}{a.due ? ` · due ${fmtDue(a.due)}` : ''}</span>
                    </span>
                    <button className={styles.smallBtn} onClick={() => openAssignment(a)}>Start</button>
                  </div>
                ))}
              {openErr ? <div className={styles.err}>{openErr}</div> : null}
            </section>

            {/* ── review + quick practice ── */}
            <section className={styles.card}>
              <div className={styles.cardHead}><span className={styles.cardLabel}>Practice</span></div>
              <button className={`${styles.reviewRow} ${dueCount ? styles.reviewHot : ''}`} onClick={() => onOpenReview(code)}>
                <ReplayIcon fontSize="small" />
                <span className={styles.reviewText}>{dueCount ? <><b>{dueCount}</b> {focusName} item{dueCount === 1 ? '' : 's'} due for review</> : `Nothing due in ${focusName} — all caught up`}</span>
                {dueCount ? <span className={styles.topicCta}>Review →</span> : null}
              </button>
              <div className={styles.quick}>
                <button className={styles.quickBtn} onClick={onOpenSpecialMode}><ScienceIcon fontSize="small" /> Special Mode</button>
                <button className={styles.quickBtn} onClick={onOpenVerbLab}><SpellcheckIcon fontSize="small" /> Verb Lab</button>
                <button className={styles.quickBtn} onClick={onOpenAiLesson}><AutoAwesomeIcon fontSize="small" /> AI Lesson</button>
                <button className={styles.quickBtn} onClick={onOpenProfile}><PersonIcon fontSize="small" /> Profile</button>
              </div>
            </section>

            {/* ── everything the app can open, from the shell's registry ── */}
            {actions.length > 0 && (
              <section className={`${styles.card} ${styles.cardWide}`}>
                <div className={styles.cardHead}><span className={styles.cardLabel}>Everything</span></div>
                <ActionTiles actions={actions} onPick={(a) => a.onClick && a.onClick()} animate={false} />
              </section>
            )}
          </div>
        </div>
      </div>

      {lesson && ReactDOM.createPortal(
        <StudentLessonView lesson={lesson.lesson} meetingId={lesson.meetingId} voiceSpeed={voiceSpeed} homework
          onClose={() => { setLesson(null); hw.refetch() }} />,
        document.body
      )}
    </>
  )
}
